import React, { useState, useEffect } from "react";
import { ShoppingCart } from "lucide-react";

interface StickyAddToCartBarProps {
  productName: string;
  price: number;
  hasDiscount: boolean;
  discountedPrice: number;
  onAddToCart: () => void;
  isOutOfStock: boolean;
  needsVariantSelection?: boolean;
}

const StickyAddToCartBar: React.FC<StickyAddToCartBarProps> = ({
  productName,
  price,
  hasDiscount,
  discountedPrice,
  onAddToCart,
  isOutOfStock,
  needsVariantSelection = false,
}) => {
  const label = isOutOfStock
    ? "Out of Stock"
    : needsVariantSelection
      ? "Select a variant"
      : "Add to Cart";
  const disabled = isOutOfStock || needsVariantSelection;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky bar after scrolling 400px
      if (window.scrollY > 400) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-section-card-bg border-t border-section-border shadow-lg shadow-black/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Product Info */}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-section-text truncate">
              {productName}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              {hasDiscount ? (
                <>
                  <span className="text-lg font-bold text-section-text">
                    ${discountedPrice.toFixed(2)}
                  </span>
                  <span className="text-sm text-section-muted line-through">
                    ${price.toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="text-lg font-bold text-section-text">
                  ${price.toFixed(2)}
                </span>
              )}
            </div>
          </div>

          {/* Add to Cart Button */}
          <button
            onClick={onAddToCart}
            disabled={disabled}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:opacity-90 disabled:bg-section-surface disabled:text-section-muted disabled:cursor-not-allowed transition-colors font-bold"
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StickyAddToCartBar;
